import { Component, type CSSProperties, type ErrorInfo, type ReactNode } from 'react';
import { EmptyState } from '../ui/EmptyState';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';

const m = defineMessages({
  noReason: {
    id: 'map.error.noReason',
    defaultMessage: 'причина не названа',
    description: 'Причина сбоя карты, когда исключение не несёт текста (строчная фраза)',
  },
  title: {
    id: 'map.error.title',
    defaultMessage: 'Карта не отрисована',
    description: 'Заголовок карточки на месте карты, когда карта упала',
  },
  reason: {
    id: 'map.error.reason',
    defaultMessage: '{reason}. Остальная страница работает: числа и таблицы читаются из того же источника, обновление страницы пересоберёт кадр.',
    description: 'Пояснение под заголовком «Карта не отрисована»; {reason} — причина сбоя без точки',
  },
});


export interface MapErrorBoundaryProps {
  children: ReactNode;
  /** Класс/высота кадра: карточка сбоя занимает место карты, а не схлопывает страницу. */
  className?: string;
  style?: CSSProperties;
}

interface MapErrorBoundaryState {
  reason: string | null;
}

/**
 * Карта — единственный кусок прототипа с императивным WebGL-рантаймом:
 * исключение в нём не должно уносить всё приложение в белый экран.
 * Границу держим локальной — падает кадр карты, страница остаётся живой.
 */
export class MapErrorBoundary extends Component<MapErrorBoundaryProps, MapErrorBoundaryState> {
  state: MapErrorBoundaryState = { reason: null };

  static getDerivedStateFromError(error: unknown): MapErrorBoundaryState {
    const reason =
      error instanceof Error && error.message
        ? error.message
        : typeof error === 'string' && error
          ? error
          : tr(m.noReason);
    return { reason };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Логируем в dev: в проде прототипа консоль не шумит, причина уже на экране.
    if (import.meta.env.DEV) {
      console.warn('[map] кадр карты упал:', error, info.componentStack);
    }
  }

  render(): ReactNode {
    const { reason } = this.state;
    if (reason === null) return this.props.children;

    return (
      <div className={this.props.className} style={this.props.style}>
        <EmptyState
          title={tr(m.title)}
          reason={tr(m.reason, { reason })}
        />
      </div>
    );
  }
}
